import { createFileRoute, Link } from "@tanstack/react-router";
import useRequest from "@/lib/request/useRequest";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import useAuth from "@/lib/auth/useAuth";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { MoistureStatus, NaveField } from "@/types/water";
import { EnrollmentTier } from "@/lib/utils/enrollment";
import { SortingState, VisibilityState } from "@tanstack/react-table";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import {
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button,
    ButtonGroup,
    Divider,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Chip,
    Input,
    Tab,
    Tabs,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBars,
    faLeaf,
    faUpRightFromSquare,
    faPenToSquare,
    faFileZipper,
    faArrowUpFromGroundWater,
    faClipboardList,
    faChevronDown,
    faExclamationCircle,
    faExclamationTriangle,
    faCheckCircle,
    faFilter,
    faSearch,
    faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { FieldInsights } from "@/types/fieldInsights";
import { ColumnDef } from "@tanstack/react-table";
import {
    createFieldColumns,
    FieldColumnOptions,
} from "@/components/fields/fieldColumns";
import TableV2 from "@/components/table/TableV2";
import { useInfiniteQuery } from "@tanstack/react-query";
import { PaginationResponse } from "@/types/pagination";
import BulkEditNitrogenPlan from "@/components/home/BulkEditNitrogenPlan";
import BulkRxExport from "@/components/home/BulkRxExport";
import NStatusChip from "@/components/common/NStatusChip";
import RecommendationChip from "@/components/common/RecommendationChip";
import BulkSetGrowthStage from "@/components/home/BulkSetGrowthStage";
import UserRecommendationForm from "@/components/field/UserRecommendationForm";
import { CropConfiguration } from "@/types/cropConfiguration";
import { roundTo } from "@/lib/utils/numbers";
import { useI18n } from "@/lib/i18n/useI18n";
import FieldNitrogenAdvisor from "@/components/field/FieldNitrogenAdvisor";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import SIPreview from "@/components/home/SIPreview";
import BulkLogIrrigation from "@/components/home/BulkLogIrrigation";
import BulkLogApplication from "@/components/home/BulkLogApplication";
import useBulkBoundaryDownload from "@/lib/utils/useBulkBoundaryDownload";
import classNames from "classnames";
import {
    getSimulationTypeColor,
    getSimulationTypeLabel,
} from "@/lib/utils/nitrogenPlan";
import { ProcessingStatus } from "@/types/fieldNitrogenPlan";
import { isFieldTrial } from "@/lib/utils/access";
import useIsMobile, { DESKTOP_BREAKPOINT } from "@/lib/utils/useIsMobile";
import MobileFilterDrawer from "@/components/home/MobileFilterDrawer";
import InsightsMobileFieldCard from "@/components/home/InsightsMobileFieldCard";
import InsightsMobileFieldDetailDrawer from "@/components/home/InsightsMobileFieldDetailDrawer";

const searchSchema = z.object({
    search: z.string().optional(),
    ordering: z.string().optional(),
    service_provider: z.string().optional(),
    grower: z.string().optional(),
    account_manager: z.string().optional(),
    enrollment_tier: z.string().optional(),
    date: z.string().optional(),
    advisor_field: z.number().optional(),
    columns: z.string().optional(),
});

export const Route = createFileRoute("/dashboard/home/insights")({
    component: RouteComponent,
    validateSearch: zodValidator(searchSchema),
});

const COLUMN_VISIBILITY_STORAGE_KEY = "insights-column-visibility";

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
    selection: true,
    grower: true,
    name: true,
    irrigation: true,
    recommendation_date: true,
    sufficiency: true,
    rate: true,
    product: true,
    model: true,
    openAdvisor: true,
    totalIrrigation: true,
    h20Sufficiency: true,
    currentWaterDeficit: true,
    forecastWaterDeficit: true,
    forecastRain: true,
};

const VALID_COLUMN_IDS = new Set(Object.keys(DEFAULT_COLUMN_VISIBILITY));

const INSIGHTS_SORT_OPTIONS = [
    { key: "sufficiency", label: "N Sufficiency" },
    { key: "recommendation_date", label: "Image Date" },
    { key: "name", label: "Name" },
    { key: "grower", label: "Farm" },
];

function parseColumnsFromUrl(
    columnsParam: string | undefined,
): VisibilityState {
    if (!columnsParam?.trim()) {
        return { ...DEFAULT_COLUMN_VISIBILITY };
    }
    const visibleIds = columnsParam
        .split(",")
        .map((id) => id.trim())
        .filter((id) => VALID_COLUMN_IDS.has(id));
    const visibility = { ...DEFAULT_COLUMN_VISIBILITY };
    for (const id of Object.keys(visibility)) {
        (visibility as Record<string, boolean>)[id] = visibleIds.includes(id);
    }
    return visibility;
}

function visibilityToColumnParam(visibility: VisibilityState): string {
    return Object.entries(visibility)
        .filter(([, visible]) => visible)
        .map(([id]) => id)
        .join(",");
}

function parseColumnsFromStorage(
    stored: string | null,
): VisibilityState | null {
    try {
        if (!stored) return null;
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        return { ...DEFAULT_COLUMN_VISIBILITY, ...parsed };
    } catch {
        return null;
    }
}

function RouteComponent() {
    const { useGetRequest, axiosRequest, useMutateRequest } = useRequest();

    const {
        search,
        ordering,
        advisor_field: advisorField,
        service_provider,
        grower,
        account_manager,
        enrollment_tier,
        date,
        columns: columnsParam,
    } = Route.useSearch();

    const navigate = Route.useNavigate();

    const { formatDateMMDDYYYY, formatUnit } = useI18n();

    const [viewAllRates, setViewAllRates] = useState(false);

    const { customRecommendationsEnabled, advisorEnabled } = useFeatureFlag();

    const { isConsultantOrSentinel, user, userIsSentinel } = useAuth();
    const { isMobile } = useIsMobile();
    const { isMobile: compactFilters } = useIsMobile(DESKTOP_BREAKPOINT);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [detailField, setDetailField] = useState<FieldInsights | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState(search ?? "");

    useEffect(() => {
        setDebouncedSearch(search ?? "");
    }, [search]);

    useEffect(() => {
        if (!compactFilters) return;
        if (debouncedSearch === (search ?? "")) return;
        const handler = setTimeout(() => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    search: debouncedSearch === "" ? undefined : debouncedSearch,
                }),
            });
        }, 700);

        return () => clearTimeout(handler);
    }, [compactFilters, debouncedSearch, navigate, search]);

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: userIsSentinel,
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        enabled: isConsultantOrSentinel,
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
        enabled: userIsSentinel,
    });

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: "crops/",
    });

    const growersById = useMemo(() => {
        return (
            growersQuery.data?.reduce(
                (acc, grower) => {
                    acc[grower.id] = grower;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [growersQuery.data]);

    const serviceProvidersById = useMemo(() => {
        return (
            serviceProvidersQuery.data?.reduce(
                (acc, serviceProvider) => {
                    acc[serviceProvider.id] = serviceProvider;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [serviceProvidersQuery.data]);

    const accountManagersById = useMemo(() => {
        return (
            accountManagersQuery.data?.reduce(
                (acc, accountManager) => {
                    acc[accountManager.id] = accountManager;
                    return acc;
                },
                {} as Record<number, User>,
            ) ?? {}
        );
    }, [accountManagersQuery.data]);

    const growerOptions = useMemo(() => {
        return (
            growersQuery.data?.map((grower) => ({
                label: grower.name,
                key: grower.id.toString(),
            })) ?? []
        );
    }, [growersQuery.data]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((serviceProvider) => ({
                label: serviceProvider.name,
                key: serviceProvider.id.toString(),
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const accountManagerOptions = useMemo(() => {
        return (
            accountManagersQuery.data?.map((accountManager) => ({
                label: accountManager.full_name,
                key: accountManager.id.toString(),
            })) ?? []
        );
    }, [accountManagersQuery.data]);

    const [sorting, setSorting] = useState<SortingState>([
        ordering
            ? {
                  id: ordering,
                  desc: ordering.startsWith("-"),
              }
            : {
                  id: "sufficiency",
                  desc: false,
              },
    ]);

    useEffect(() => {
        if (sorting.length > 0) {
            const id = sorting[0].id.startsWith("-")
                ? sorting[0].id.slice(1)
                : sorting[0].id;
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    const query = useInfiniteQuery<PaginationResponse<FieldInsights>>({
        queryKey: [
            "field_nitrogen_plans",
            search,
            ordering || "sufficiency",
            service_provider,
            grower,
            account_manager,
            enrollment_tier,
            date,
        ],
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<FieldInsights>>({
                url: "fields/insights/",
                params: {
                    search,
                    ordering: ordering || "sufficiency",
                    service_provider,
                    grower,
                    account_manager,
                    enrollment_tier,
                    page: pageParam,
                    size: 40,
                },
            });
        },
    });

    const insightsData = useMemo(() => {
        return query.data?.pages.flatMap((page) => page.results) ?? [];
    }, [query.data]);

    const fieldsHaveWaterInsights = useMemo(() => {
        return insightsData.some(
            (field) =>
                field.active_enrollment?.enrollment_tier ===
                    EnrollmentTier.Irrigation ||
                field.active_enrollment?.enrollment_tier ===
                    EnrollmentTier.Complete,
        );
    }, [insightsData]);

    const waterQuery = useGetRequest<NaveField[]>({
        url: "fields/insights/water/",
        enabled: fieldsHaveWaterInsights,
    });

    const tableData = useMemo(() => {
        return insightsData.map((field) => ({
            ...field,
            water: waterQuery.data?.find((water) => water.id === field.id),
        }));
    }, [insightsData, waterQuery.data]);

    const setOpenAdvisorFieldId = useCallback(
        (fieldId: number | undefined) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    advisor_field: fieldId,
                }),
            });
        },
        [navigate],
    );

    const [editNitrogenPlan, setEditNitrogenPlan] = useState(false);

    const insightsColumns: ColumnDef<FieldInsights>[] = useMemo(() => {
        const columnOptions: FieldColumnOptions = {
            growersById,
            serviceProvidersById,
            accountManagersById,
            userIsSentinel,
        };

        const cols: ColumnDef<FieldInsights>[] = [
            createFieldColumns.selection<FieldInsights>(),
            createFieldColumns.growerAndServiceProvider<FieldInsights>(
                columnOptions,
            ),
            createFieldColumns.fieldName<FieldInsights>(),
            createFieldColumns.irrigation<FieldInsights>(),
            {
                id: "recommendation_date",
                header: "Image Date",
                accessorFn: (row) => {
                    return row.recommendation?.latest_image_capture_date;
                },
                cell: (info) => {
                    if (
                        info.row.original.active_enrollment?.enrollment_tier ===
                        EnrollmentTier.Irrigation
                    ) {
                        return null;
                    }
                    if (!info.getValue()) {
                        return null;
                    }
                    return (
                        <div className="text-sm">
                            {formatDateMMDDYYYY(info.getValue() as string)}
                        </div>
                    );
                },
                size: 90,
            },
            {
                id: "sufficiency",
                header: "N Sufficiency",
                accessorFn: (row) => row.recommendation.si_mean,
                cell: ({ row }) => {
                    if (
                        row.original.active_enrollment?.enrollment_tier ===
                        EnrollmentTier.Irrigation
                    ) {
                        return null;
                    }
                    // link to field insights dashboard
                    return user && isFieldTrial(row.original, user) ? (
                        <Chip color="warning" variant="flat" radius="sm">
                            Trial Field
                        </Chip>
                    ) : (
                        <div className="flex flex-col gap-1 mx-1">
                            <div className="w-full">
                                <RecommendationChip
                                    recommendation={row.original.recommendation}
                                    notes={
                                        row.original.recommendation
                                            .user_recommendation?.notes ??
                                        undefined
                                    }
                                    user={
                                        row.original.recommendation
                                            .user_recommendation?.user ??
                                        undefined
                                    }
                                    deferDoNotApply={true}
                                    fullWidth={true}
                                />
                            </div>
                            {row.original.recommendation?.si_mean && (
                                <div className="w-full">
                                    <NStatusChip
                                        nStatus={
                                            row.original.recommendation.n_status
                                        }
                                        sufficiency={roundTo(
                                            row.original.recommendation
                                                .si_mean * 100,
                                            1,
                                        )}
                                        fullWidth={true}
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
                size: 150,
            },
            {
                id: "growthStage",
                header: "Growth Stage",
                accessorFn: (row) =>
                    row.default_nitrogen_plan_simulation
                        ?.target_growth_stage_label,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col w-full gap-1 text-xs px-2">
                            <Chip
                                size="sm"
                                radius="sm"
                                classNames={{
                                    base: "text-[0.65rem]",
                                }}
                                variant="flat"
                            >
                                {row.original.crop_stage?.growth_stage}
                            </Chip>
                            <div className="font-bold">
                                {
                                    row.original.active_planting_operation
                                        .crop_configuration.crop_name
                                }
                            </div>
                        </div>
                    );
                },
                size: 140,
            },
            {
                id: "nAppliedSoFar",
                header: "N Applied",
                accessorFn: (row) => row.n_applied_so_far,
                cell: ({ row }) => {
                    return (
                        <Link
                            to="/dashboard/fields/$fieldId/operations"
                            params={{ fieldId: row.original.id.toString() }}
                            target="_blank"
                            className="flex flex-col w-full gap-1 text-xs px-2"
                        >
                            <Chip
                                size="sm"
                                radius="sm"
                                classNames={{
                                    base: "text-[0.65rem]",
                                }}
                                variant="flat"
                                startContent={
                                    row.original.n_applied_so_far === 0 ? (
                                        <FontAwesomeIcon
                                            size="xs"
                                            icon={faExclamationTriangle}
                                            className="text-yellow-500"
                                        />
                                    ) : null
                                }
                            >
                                {row.original.n_applied_so_far}{" "}
                                {formatUnit("lb_ac", "kg_ha")}
                            </Chip>
                        </Link>
                    );
                },
                size: 100,
            },
            {
                id: "rate",
                enableSorting: false,
                header: "Rate",
                accessorFn: (row) => row.default_nitrogen_plan_simulation?.id,
                cell: ({ row, table }) => {
                    if (
                        row.original.active_enrollment?.enrollment_tier ===
                        EnrollmentTier.Irrigation
                    ) {
                        return null;
                    }
                    if (user && isFieldTrial(row.original, user)) {
                        return (
                            <Chip color="warning" variant="flat" radius="sm">
                                Trial Field
                            </Chip>
                        );
                    }

                    if (
                        !row.original.recommendation.recommend_fertigation &&
                        !viewAllRates
                    ) {
                        return (
                            <div className="text-xs px-2 italic">
                                No recommendation
                            </div>
                        );
                    }
                    const plan = row.original.default_nitrogen_plan_simulation;

                    if (!plan) return null;

                    if (plan?.processing_status !== ProcessingStatus.COMPLETED)
                        return (
                            <div className="text-xs px-2  flex gap-1 items-center">
                                <FontAwesomeIcon
                                    size="sm"
                                    icon={faExclamationTriangle}
                                    className="text-yellow-500"
                                />
                                No rate found
                            </div>
                        );

                    return (
                        <div className="flex flex-row w-full text-xs px-2 gap-1 cursor-pointer items-center justify-between">
                            <div className="grid grid-cols-[auto_auto] gap-y-1 items-center gap-x-1">
                                <div>Scenario:</div>
                                <div
                                    className="rounded-md flex items-center justify-center p-1 font-bold "
                                    style={{
                                        backgroundColor: getSimulationTypeColor(
                                            plan.simulation_type,
                                        ),
                                    }}
                                >
                                    {getSimulationTypeLabel(
                                        plan.simulation_type,
                                    )}
                                </div>
                                <div>Target:</div>
                                <Chip
                                    size="sm"
                                    radius="sm"
                                    classNames={{
                                        base: "text-[0.65rem]",
                                    }}
                                    variant="flat"
                                >
                                    {plan.target_growth_stage_label}
                                </Chip>
                            </div>
                            <Chip
                                size="sm"
                                radius="sm"
                                color="primary"
                                onClick={() => {
                                    table.toggleAllRowsSelected(false);
                                    row.toggleSelected(true);
                                    setEditNitrogenPlan(true);
                                }}
                            >
                                {roundTo(plan.current_rate, 1)}{" "}
                                {formatUnit("lb_ac", "kg_ha")}
                            </Chip>
                        </div>
                    );
                },
                size: 250,
            },
            {
                enableSorting: false,
                id: "openAdvisor",
                header: "Advisor",
                accessorFn: (row) => row.id,
                cell: ({ row }) => {
                    if (
                        row.original.active_enrollment?.enrollment_tier ===
                        EnrollmentTier.Irrigation
                    ) {
                        return null;
                    }
                    return (
                        <div>
                            <Button
                                variant="flat"
                                size="sm"
                                color="primary"
                                onPress={() =>
                                    setOpenAdvisorFieldId(row.original.id)
                                }
                                startContent={<FontAwesomeIcon icon={faLeaf} />}
                                isDisabled={
                                    user && isFieldTrial(row.original, user)
                                }
                            >
                                Advisor
                            </Button>
                        </div>
                    );
                },
                size: 100,
            },
        ];

        if (isConsultantOrSentinel || advisorEnabled) {
            return cols;
        }
        return cols.filter((col) => col.id !== "openAdvisor");
    }, [
        growersById,
        serviceProvidersById,
        accountManagersById,
        userIsSentinel,
        isConsultantOrSentinel,
        advisorEnabled,
        formatDateMMDDYYYY,
        user,
        formatUnit,
        viewAllRates,
        setOpenAdvisorFieldId,
    ]);

    const waterColumns: ColumnDef<FieldInsights>[] = useMemo(
        () => [
            {
                id: "h2oSufficiency",
                header: "H2O Sufficiency",
                accessorFn: (row) =>
                    row.water?.water_estimate?.capacity_percentage,
                cell: ({ row }) => {
                    return user && isFieldTrial(row.original, user) ? (
                        <Chip color="warning" variant="flat" radius="sm">
                            Trial Field
                        </Chip>
                    ) : (
                        <div className="flex flex-col gap-1 mx-1 items-center">
                            <div className="w-full">
                                {row.original.water?.water_estimate
                                    ?.moisture_status ===
                                    MoistureStatus.APPROCHING_DEFICIENCY && (
                                    <Chip
                                        size="sm"
                                        radius="sm"
                                        variant="flat"
                                        classNames={{
                                            base: "px-2 text-red-700 bg-red-100 w-full max-w-full flex justify-center",
                                            content:
                                                "font-bold w-full text-center",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faExclamationCircle}
                                            className="mr-1"
                                        />
                                        Irrigate
                                    </Chip>
                                )}
                            </div>
                            <div className="w-full">
                                <Chip
                                    size="sm"
                                    radius="sm"
                                    variant="flat"
                                    classNames={{
                                        base: classNames(
                                            "px-2 w-full max-w-full flex justify-center",
                                            {
                                                "bg-green-100 text-green-700":
                                                    row.original.water
                                                        ?.water_estimate
                                                        ?.moisture_status ===
                                                    "Optimal",
                                                "bg-blue-100 text-blue-700":
                                                    row.original.water
                                                        ?.water_estimate
                                                        ?.moisture_status ===
                                                    MoistureStatus.APPROACHING_SATURATION,
                                                "bg-yellow-100 text-yellow-700":
                                                    row.original.water
                                                        ?.water_estimate
                                                        ?.moisture_status ===
                                                    MoistureStatus.APPROCHING_DEFICIENCY,
                                            },
                                        ),
                                        content: "font-bold w-full text-center",
                                    }}
                                >
                                    {row.original.water?.water_estimate?.capacity_percentage?.toFixed(
                                        1,
                                    ) ?? "-"}{" "}
                                    %
                                </Chip>
                            </div>
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "currentWaterDeficit",
                header: "Current Deficit",
                accessorFn: (row) => row.water?.water_estimate?.deficit,
                cell: ({ row }) => {
                    return row.original.water &&
                        user &&
                        !isFieldTrial(row.original, user) ? (
                        <Chip
                            size="sm"
                            radius="sm"
                            classNames={{
                                base: classNames("px-2", {
                                    "bg-green-100 text-green-700":
                                        row.original.water?.water_estimate
                                            ?.deficit < 0,
                                    "bg-yellow-100 text-yellow-700":
                                        row.original.water?.water_estimate
                                            ?.deficit >= 0,
                                }),
                                content: "font-bold",
                            }}
                        >
                            {row.original.water?.water_estimate?.deficit > 0
                                ? "+"
                                : ""}{" "}
                            {row.original.water?.water_estimate?.deficit?.toFixed(
                                2,
                            ) ?? "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    ) : (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color="default"
                            classNames={{ base: "px-2", content: "font-bold" }}
                        >
                            -
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "forecastWaterDeficit",
                header: "7 Day Deficit Forecast",
                accessorFn: (row) =>
                    row.water?.water_estimate?.deficit_forecast,
                cell: ({ row }) => {
                    return row.original.water &&
                        user &&
                        !isFieldTrial(row.original, user) ? (
                        <Chip
                            size="sm"
                            radius="sm"
                            classNames={{
                                base: classNames("px-2", {
                                    "bg-green-100 text-green-700":
                                        row.original.water?.water_estimate
                                            ?.deficit_forecast < 0,
                                    "bg-yellow-100 text-yellow-700":
                                        row.original.water?.water_estimate
                                            ?.deficit_forecast >= 0,
                                }),
                                content: "font-bold",
                            }}
                        >
                            {row.original.water?.water_estimate
                                ?.deficit_forecast > 0
                                ? "+"
                                : ""}{" "}
                            {row.original.water?.water_estimate?.deficit_forecast?.toFixed(
                                2,
                            ) ?? "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    ) : (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color="default"
                            classNames={{ base: "px-2", content: "font-bold" }}
                        >
                            -
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "totalIrrigation",
                header: "Total Irrigation",
                accessorFn: (row) => row.water?.cumulativeIrrigation,
                cell: ({ row }) => {
                    return (
                        <Chip
                            radius="sm"
                            as={Link}
                            to={`/dashboard/fields/${row.original.id}/operations`}
                            target="_blank"
                            variant="flat"
                            className="text-xs px-2"
                            color={row.original.water ? "primary" : "default"}
                            classNames={{ content: "font-bold" }}
                            endContent={
                                <FontAwesomeIcon
                                    icon={faUpRightFromSquare}
                                    size="sm"
                                    className="mr-2"
                                />
                            }
                        >
                            {row.original.water?.cumulativeIrrigation?.toFixed(
                                2,
                            ) ?? "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "forecastRain",
                header: "Rain Forecast",
                accessorFn: (row) => row.water?.expectedRain,
                cell: ({ row }) => {
                    return (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color={
                                row.original.water?.expectedRain
                                    ? "primary"
                                    : "default"
                            }
                            classNames={{ base: "px-2", content: "font-bold" }}
                        >
                            {row.original.water?.expectedRain?.toFixed(2) ??
                                "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    );
                },
                size: 100,
            },
        ],
        [formatUnit, user],
    );

    const columns = useMemo(() => {
        return fieldsHaveWaterInsights
            ? [...insightsColumns, ...waterColumns]
            : insightsColumns;
    }, [insightsColumns, waterColumns, fieldsHaveWaterInsights]);

    const [selectedFields, setSelectedFields] = useState<FieldInsights[]>([]);
    const selectedFieldIds = useMemo(() => {
        return selectedFields.map((field) => field.id);
    }, [selectedFields]);
    const { downloadBoundaries, isDownloading } =
        useBulkBoundaryDownload(selectedFieldIds);

    const handleSelectionChange = (selectedData: FieldInsights[]) => {
        setSelectedFields(selectedData);
    };

    const selectedFieldsIdsByCropConfiguration = useMemo(() => {
        const mapping: Record<number, number[]> = {};
        selectedFields.forEach((field) => {
            const cropConfigId =
                field.active_planting_operation.crop_configuration.id;
            if (!mapping[cropConfigId]) {
                mapping[cropConfigId] = [];
            }
            mapping[cropConfigId].push(field.id!);
        });
        return mapping;
    }, [selectedFields]);

    const isLoading = useMemo(() => {
        return (
            serviceProvidersQuery.isLoading ||
            growersQuery.isLoading ||
            accountManagersQuery.isLoading ||
            query.isLoading
        );
    }, [
        serviceProvidersQuery.isLoading,
        growersQuery.isLoading,
        accountManagersQuery.isLoading,
        query.isLoading,
    ]);

    const mobileIsLoading = query.isLoading && tableData.length === 0;

    const activeMobileFilterCount = useMemo(() => {
        return [
            search?.trim(),
            service_provider,
            grower,
            account_manager,
        ].filter(Boolean).length;
    }, [account_manager, grower, search, service_provider]);

    const [exportRxOpen, setExportRxOpen] = useState(false);
    const [bulkSetGrowthStageOpen, setBulkSetGrowthStageOpen] = useState(false);
    const [editRecommendationOpen, setEditRecommendationOpen] = useState(false);
    const prescriptionsQuery = useMutateRequest<
        {
            field_id: number;
            field_name: string;
            simulation_id: number;
            prescription_id: number;
            prescription_name: string;
        }[]
    >({
        url: "field_nitrogen_plans/simulations_prescriptions/",
        method: "put",
    });
    const [prescriptionsToExport, setPrescriptionsToExport] = useState<
        {
            field_id: number;
            field_name: string;
            simulation_id: number;
            prescription_id: number;
            prescription_name: string;
        }[]
    >([]);
    const [bulkLogIrrigationOpen, setBulkLogIrrigationOpen] = useState(false);
    const [bulkLogApplicationOpen, setBulkLogApplicationOpen] = useState(false);
    const [bulkSetSensitivityOpen, setBulkSetSensitivityOpen] = useState(false);
    const [bulkSensitivity, setBulkSensitivity] = useState<
        "low" | "moderate" | "high"
    >("low");
    const [bulkInsightsActive, setBulkInsightsActive] = useState("auto");
    const [bulkNotificationsActive, setBulkNotificationsActive] =
        useState("auto");

    const patchFieldsBulk = useMutateRequest({
        method: "patch",
        url: "/fields/bulk_update/",
    });

    const selectedField = useMemo(() => {
        return tableData.find(
            (field) => field.id.toString() === advisorField?.toString(),
        );
    }, [tableData, advisorField]);

    const loadedFromStorageRef = useRef(false);

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        () => {
            if (columnsParam?.trim()) {
                return parseColumnsFromUrl(columnsParam);
            }
            const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
            const fromStorage = parseColumnsFromStorage(stored);
            if (fromStorage) {
                loadedFromStorageRef.current = true;
                return fromStorage;
            }
            return { ...DEFAULT_COLUMN_VISIBILITY };
        },
    );

    const handleColumnVisibilityChange = (
        updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => {
        setColumnVisibility((prev) => {
            const next =
                typeof updater === "function" ? updater(prev) : updater;
            localStorage.setItem(
                COLUMN_VISIBILITY_STORAGE_KEY,
                JSON.stringify(next),
            );
            navigate({
                search: (prevSearch) => ({
                    ...prevSearch,
                    columns: visibilityToColumnParam(next),
                }),
                replace: true,
            });
            return next;
        });
    };

    useEffect(() => {
        if (loadedFromStorageRef.current && !columnsParam?.trim()) {
            loadedFromStorageRef.current = false;
            navigate({
                search: (prev) => ({
                    ...prev,
                    columns: visibilityToColumnParam(columnVisibility),
                }),
                replace: true,
            });
        }
    }, [columnVisibility, columnsParam, navigate]);

    const selectedColumns = useMemo(() => {
        return Object.keys(columnVisibility).filter(
            (key) => columnVisibility[key as keyof VisibilityState],
        );
    }, [columnVisibility]);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {compactFilters ? (
                <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                    <Button
                        size="sm"
                        color={
                            activeMobileFilterCount > 0 ? "primary" : "default"
                        }
                        onPress={() => setMobileFiltersOpen(true)}
                        startContent={<FontAwesomeIcon icon={faFilter} />}
                    >
                        Filters
                        {activeMobileFilterCount > 0
                            ? ` (${activeMobileFilterCount})`
                            : ""}
                    </Button>
                    <Input
                        size="sm"
                        placeholder="Search..."
                        value={debouncedSearch}
                        onValueChange={setDebouncedSearch}
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        className="flex-1"
                    />
                    {isConsultantOrSentinel && (
                        <Button
                            size="sm"
                            color={viewAllRates ? "primary" : "default"}
                            startContent={
                                viewAllRates ? (
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                ) : (
                                    <FontAwesomeIcon icon={faCircle} />
                                )
                            }
                            onPress={() => setViewAllRates(!viewAllRates)}
                        >
                            View Rates
                        </Button>
                    )}
                </div>
            ) : (
                <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                    <div className="flex flex-row gap-2 items-center">
                        <FieldFilterBar
                            searchValue={search}
                            onSearchChange={(value) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        search:
                                            value === "" ? undefined : value,
                                    }),
                                });
                            }}
                            serviceProviderId={service_provider}
                            onServiceProviderChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        service_provider: id ?? "",
                                    }),
                                });
                            }}
                            serviceProviderOptions={serviceProviderOptions}
                            growerId={grower}
                            onGrowerChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        grower: id ?? "",
                                    }),
                                });
                            }}
                            growerOptions={growerOptions}
                            accountManagerId={account_manager}
                            onAccountManagerChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        account_manager: id ?? "",
                                    }),
                                });
                            }}
                            accountManagerOptions={accountManagerOptions}
                            enrollmentTier={enrollment_tier}
                            onEnrollmentTierChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        enrollment_tier: id ?? undefined,
                                    }),
                                });
                            }}
                        />
                        <Dropdown>
                            <DropdownTrigger>
                                <Button size="sm">
                                    Columns{" "}
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                disallowEmptySelection
                                closeOnSelect={false}
                                selectionMode="multiple"
                                selectedKeys={selectedColumns}
                            >
                                <DropdownItem
                                    key="grower"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                grower: !prev.grower,
                                            }),
                                        )
                                    }
                                >
                                    Farm/CSP
                                </DropdownItem>
                                <DropdownItem
                                    key="name"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                name: !prev.name,
                                            }),
                                        )
                                    }
                                >
                                    Name
                                </DropdownItem>
                                <DropdownItem
                                    key="irrigation"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                irrigation: !prev.irrigation,
                                            }),
                                        )
                                    }
                                >
                                    Irrigation
                                </DropdownItem>
                                <DropdownItem
                                    key="recommendation_date"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                recommendation_date:
                                                    !prev.recommendation_date,
                                            }),
                                        )
                                    }
                                >
                                    Image Date
                                </DropdownItem>
                                <DropdownItem
                                    key="sufficiency"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                sufficiency: !prev.sufficiency,
                                            }),
                                        )
                                    }
                                >
                                    Sufficiency
                                </DropdownItem>
                                <DropdownItem
                                    key="rate"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                rate: !prev.rate,
                                            }),
                                        )
                                    }
                                >
                                    Rate
                                </DropdownItem>
                                <DropdownItem
                                    key="product"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                product: !prev.product,
                                            }),
                                        )
                                    }
                                >
                                    Product
                                </DropdownItem>
                                <DropdownItem
                                    key="model"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                model: !prev.model,
                                            }),
                                        )
                                    }
                                >
                                    Model
                                </DropdownItem>
                                <DropdownItem
                                    key="openAdvisor"
                                    onPress={() =>
                                        handleColumnVisibilityChange(
                                            (prev) => ({
                                                ...prev,
                                                openAdvisor: !prev.openAdvisor,
                                            }),
                                        )
                                    }
                                >
                                    Advisor
                                </DropdownItem>
                                <DropdownItem
                                    key="download-shapefiles"
                                    onPress={downloadBoundaries}
                                    isDisabled={isDownloading}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faFileZipper}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    {isDownloading
                                        ? "Downloading Shapefiles"
                                        : "Download Shapefiles"}
                                </DropdownItem>
                                {fieldsHaveWaterInsights ? (
                                    <>
                                        <DropdownItem
                                            key="totalIrrigation"
                                            onPress={() =>
                                                handleColumnVisibilityChange(
                                                    (prev) => ({
                                                        ...prev,
                                                        totalIrrigation:
                                                            !prev.totalIrrigation,
                                                    }),
                                                )
                                            }
                                        >
                                            Total Irrigation
                                        </DropdownItem>
                                        <DropdownItem
                                            key="h20Sufficiency"
                                            onPress={() =>
                                                handleColumnVisibilityChange(
                                                    (prev) => ({
                                                        ...prev,
                                                        h20Sufficiency:
                                                            !prev.h20Sufficiency,
                                                    }),
                                                )
                                            }
                                        >
                                            H20 Sufficiency
                                        </DropdownItem>
                                        <DropdownItem
                                            key="currentWaterDeficit"
                                            onPress={() =>
                                                handleColumnVisibilityChange(
                                                    (prev) => ({
                                                        ...prev,
                                                        currentWaterDeficit:
                                                            !prev.currentWaterDeficit,
                                                    }),
                                                )
                                            }
                                        >
                                            Current Deficit
                                        </DropdownItem>
                                        <DropdownItem
                                            key="forecastWaterDeficit"
                                            onPress={() =>
                                                handleColumnVisibilityChange(
                                                    (prev) => ({
                                                        ...prev,
                                                        forecastWaterDeficit:
                                                            !prev.forecastWaterDeficit,
                                                    }),
                                                )
                                            }
                                        >
                                            Forecast Water Deficit
                                        </DropdownItem>
                                        <DropdownItem
                                            key="forecastRain"
                                            onPress={() =>
                                                handleColumnVisibilityChange(
                                                    (prev) => ({
                                                        ...prev,
                                                        forecastRain:
                                                            !prev.forecastRain,
                                                    }),
                                                )
                                            }
                                        >
                                            Forecast Rain
                                        </DropdownItem>
                                    </>
                                ) : null}
                            </DropdownMenu>
                        </Dropdown>
                        {isConsultantOrSentinel && (
                            <Button
                                size="sm"
                                color={viewAllRates ? "primary" : "default"}
                                startContent={
                                    viewAllRates ? (
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    ) : (
                                        <FontAwesomeIcon icon={faCircle} />
                                    )
                                }
                                onPress={() => setViewAllRates(!viewAllRates)}
                            >
                                View Rates
                            </Button>
                        )}
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    color="primary"
                                    size="sm"
                                    startContent={
                                        <FontAwesomeIcon icon={faBars} />
                                    }
                                    isDisabled={selectedFields.length === 0}
                                >
                                    Actions
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu>
                                {customRecommendationsEnabled &&
                                isConsultantOrSentinel ? (
                                    <DropdownItem
                                        key="edit-recommendation"
                                        onPress={() =>
                                            setEditRecommendationOpen(true)
                                        }
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faPenToSquare}
                                                className="text-primary-500"
                                            />
                                        }
                                    >
                                        Edit Recommendation
                                    </DropdownItem>
                                ) : null}
                                <DropdownItem
                                    key="edit-rate"
                                    onPress={() => setEditNitrogenPlan(true)}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Edit Rate/Scenario
                                </DropdownItem>

                                <DropdownItem
                                    key="set-growth-stage"
                                    onPress={() =>
                                        setBulkSetGrowthStageOpen(true)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faLeaf}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Edit Growth Stage
                                </DropdownItem>
                                <DropdownItem
                                    key="edit-insights-sensitivity"
                                    onPress={() =>
                                        setBulkSetSensitivityOpen(true)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faSliders}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Edit Insights/Notifications
                                </DropdownItem>
                                <DropdownItem
                                    key="export"
                                    onPress={() => {
                                        setExportRxOpen(true);
                                        prescriptionsQuery.mutate(
                                            {
                                                data: {
                                                    simulation_ids:
                                                        selectedFields
                                                            .map(
                                                                (field) =>
                                                                    field
                                                                        .default_nitrogen_plan_simulation
                                                                        ?.id,
                                                            )
                                                            .filter(
                                                                (
                                                                    id,
                                                                ): id is number =>
                                                                    id !==
                                                                    undefined,
                                                            ),
                                                },
                                            },
                                            {
                                                onSuccess: (data) => {
                                                    setPrescriptionsToExport(
                                                        data,
                                                    );
                                                },
                                            },
                                        );
                                    }}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faFileZipper}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Export Rx
                                </DropdownItem>
                                <DropdownItem
                                    key="log-application"
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faClipboardList}
                                            className="text-primary-500"
                                        />
                                    }
                                    onPress={() =>
                                        setBulkLogApplicationOpen(true)
                                    }
                                >
                                    Log Application
                                </DropdownItem>
                                <DropdownItem
                                    key="log-irrigation"
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faArrowUpFromGroundWater}
                                            className="text-primary-500"
                                        />
                                    }
                                    onPress={() =>
                                        setBulkLogIrrigationOpen(true)
                                    }
                                >
                                    Log Irrigation
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </div>
            )}
            <Divider />
            {isMobile ? (
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {mobileIsLoading ? (
                        <div className="p-4 text-sm text-gray-500">
                            Loading insights...
                        </div>
                    ) : tableData.length === 0 ? (
                        <div className="p-4 text-sm text-gray-500">
                            No insights found.
                        </div>
                    ) : (
                        <>
                            {tableData.map((field) => (
                                <div key={field.id} className="w-full p-1 pb-0">
                                    <InsightsMobileFieldCard
                                        field={field}
                                        growersById={growersById}
                                        isConsultantOrSentinel={
                                            isConsultantOrSentinel
                                        }
                                        user={user}
                                        formatUnit={formatUnit}
                                        viewAllRates={viewAllRates}
                                        onPress={() => setDetailField(field)}
                                    />
                                </div>
                            ))}

                            {query.hasNextPage && (
                                <div className="p-2">
                                    <Button
                                        fullWidth
                                        size="sm"
                                        isLoading={query.isFetchingNextPage}
                                        onPress={() =>
                                            void query.fetchNextPage()
                                        }
                                    >
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <TableV2
                    data={tableData}
                    columns={columns}
                    sorting={sorting}
                    setSorting={setSorting}
                    manualSorting={true}
                    isLoading={isLoading}
                    onSelectionChange={handleSelectionChange}
                    hasNextPage={query.hasNextPage}
                    fetchNextPage={query.fetchNextPage}
                    isFetchingNextPage={query.isFetchingNextPage}
                    showEndMessage={!query.hasNextPage}
                    paddingBottom="20rem"
                    pinnedColumns={["selection", "grower", "name"]}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={handleColumnVisibilityChange}
                    showScrollbar={true}
                />
            )}
            {exportRxOpen && prescriptionsToExport.length > 0 && (
                <BulkRxExport
                    prescriptionsToExport={prescriptionsToExport.map(
                        (item) => ({
                            fieldId: item.field_id,
                            prescriptionId: item.prescription_id,
                            fieldName: item.field_name,
                        }),
                    )}
                    onFinish={() => setExportRxOpen(false)}
                />
            )}

            {bulkSetGrowthStageOpen && (
                <BulkSetGrowthStage
                    cropConfigurations={cropsQuery.data || []}
                    fieldIdsByCropConfigurationId={
                        selectedFieldsIdsByCropConfiguration
                    }
                    onFinish={() => setBulkSetGrowthStageOpen(false)}
                />
            )}

            {editRecommendationOpen && (
                <UserRecommendationForm
                    fieldIds={selectedFields.map((field) => field.id!)}
                    onClose={() => {
                        setEditRecommendationOpen(false);
                        query.refetch();
                    }}
                />
            )}

            {advisorField && (
                <Modal
                    isOpen
                    onOpenChange={(isOpen) =>
                        setOpenAdvisorFieldId(isOpen ? advisorField : undefined)
                    }
                    classNames={{
                        closeButton: "mr-4",
                    }}
                    onClose={() => setOpenAdvisorFieldId(undefined)}
                    size="full"
                >
                    <ModalContent>
                        <ModalBody className="md:grid md:grid-cols-[1fr_auto_1fr] md:gap-8 flex flex-col md:h-full overflow-y-scroll p-2">
                            {selectedField && (
                                <>
                                    <SIPreview selectedField={selectedField} />
                                    <Divider
                                        orientation="vertical"
                                        className="hidden md:block"
                                    />
                                    <FieldNitrogenAdvisor
                                        fieldId={advisorField.toString()}
                                        onClose={() =>
                                            setOpenAdvisorFieldId(undefined)
                                        }
                                        hideCloseButton
                                    />
                                </>
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}

            {bulkLogIrrigationOpen && (
                <BulkLogIrrigation
                    fields={selectedFields}
                    onFinish={() => {
                        setBulkLogIrrigationOpen(false);
                        query.refetch();
                    }}
                />
            )}

            {bulkLogApplicationOpen && (
                <BulkLogApplication
                    fields={selectedFields}
                    onFinish={() => {
                        setBulkLogApplicationOpen(false);
                        query.refetch();
                    }}
                />
            )}

            {editNitrogenPlan && selectedFields.length > 0 && (
                <BulkEditNitrogenPlan
                    fieldsWithPlan={selectedFields}
                    onFinish={() => {
                        setEditNitrogenPlan(false);
                        query.refetch();
                    }}
                />
            )}

            <MobileFilterDrawer
                isOpen={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                searchValue={search}
                serviceProviderId={service_provider}
                growerId={grower}
                accountManagerId={account_manager}
                showServiceProviderFilter={userIsSentinel}
                showGrowerFilter={isConsultantOrSentinel}
                showAccountManagerFilter={userIsSentinel}
                showRecentlyCreated={false}
                serviceProviderOptions={serviceProviderOptions}
                growerOptions={growerOptions}
                accountManagerOptions={accountManagerOptions}
                ordering={ordering}
                sortOptions={INSIGHTS_SORT_OPTIONS}
                onApply={({
                    searchValue,
                    serviceProviderId,
                    growerId,
                    accountManagerId,
                    sortKey,
                    sortDesc,
                }) => {
                    if (sortKey) {
                        setSorting([{ id: sortKey, desc: sortDesc }]);
                    }
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            search: searchValue,
                            service_provider: serviceProviderId ?? "",
                            grower: growerId ?? "",
                            account_manager: accountManagerId ?? "",
                            date: undefined,
                        }),
                    });
                }}
                onClear={() => {
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            search: "",
                            service_provider: "",
                            grower: "",
                            account_manager: "",
                            date: undefined,
                        }),
                    });
                }}
            />

            <InsightsMobileFieldDetailDrawer
                field={detailField}
                isOpen={!!detailField}
                onClose={() => setDetailField(null)}
                growersById={growersById}
                serviceProvidersById={serviceProvidersById}
                accountManagersById={accountManagersById}
                isConsultantOrSentinel={isConsultantOrSentinel}
                userIsSentinel={userIsSentinel}
                user={user}
                formatDateMMDDYYYY={formatDateMMDDYYYY}
                formatUnit={formatUnit}
                showAdvisor={isConsultantOrSentinel || advisorEnabled}
                onOpenAdvisor={(fieldId) => setOpenAdvisorFieldId(fieldId)}
                viewAllRates={viewAllRates}
            />
            <Modal
                isOpen={bulkSetSensitivityOpen}
                onClose={() => setBulkSetSensitivityOpen(false)}
                size="lg"
            >
                <ModalContent>
                    <ModalHeader>Edit Insights/Notifications</ModalHeader>
                    <ModalBody className="flex flex-col gap-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
                            <div>
                                <div className="font-semibold">
                                    Insights Status
                                </div>
                                <Tabs
                                    selectedKey={bulkInsightsActive}
                                    onSelectionChange={(key) =>
                                        setBulkInsightsActive(key as string)
                                    }
                                >
                                    <Tab key="auto" title="Auto" />
                                    <Tab key="active" title="Active" />
                                    <Tab key="inactive" title="Inactive" />
                                </Tabs>
                            </div>
                            <div>
                                <div className="font-semibold">
                                    Notifications Active
                                </div>
                                <Tabs
                                    selectedKey={bulkNotificationsActive}
                                    onSelectionChange={(key) =>
                                        setBulkNotificationsActive(
                                            key as string,
                                        )
                                    }
                                >
                                    <Tab key="auto" title="Auto" />
                                    <Tab key="active" title="Active" />
                                    <Tab key="inactive" title="Inactive" />
                                </Tabs>
                            </div>
                        </div>
                        <div>
                            <div className="font-semibold mb-2">
                                Insights Status
                            </div>
                            <ButtonGroup fullWidth>
                                {(
                                    [
                                        { value: "low", label: "Regular" },
                                        {
                                            value: "moderate",
                                            label: "Moderate",
                                        },
                                        { value: "high", label: "High" },
                                    ] as const
                                ).map(({ value, label }) => (
                                    <Button
                                        key={value}
                                        color={
                                            bulkSensitivity === value
                                                ? "primary"
                                                : "default"
                                        }
                                        onPress={() =>
                                            setBulkSensitivity(value)
                                        }
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            isLoading={patchFieldsBulk.isPending}
                            onPress={() => {
                                const insights_active_manual =
                                    bulkInsightsActive === "auto"
                                        ? null
                                        : bulkInsightsActive === "active";
                                const notifications_active_manual =
                                    bulkNotificationsActive === "auto"
                                        ? null
                                        : bulkNotificationsActive === "active";
                                patchFieldsBulk.mutate(
                                    {
                                        data: selectedFields.map((field) => ({
                                            id: field.id,
                                            insight_sensitivity:
                                                bulkSensitivity,
                                            insights_active_manual,
                                            notifications_active_manual,
                                        })),
                                    },
                                    {
                                        onSuccess: () => {
                                            setBulkSetSensitivityOpen(false);
                                            query.refetch();
                                        },
                                    },
                                );
                            }}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}
